import React from "react";
import {
    faArrowTurnDownLeft,
    faChevronRight,
    faCloud,
    faServer,
} from "@fortawesome/pro-solid-svg-icons";
import { box, Flex } from "@singlestore/fusion/components/layout";
import { H2, Paragraph } from "@singlestore/fusion/components/typography";
import { cva } from "class-variance-authority";
import { useSearchModalContext } from "contexts/SearchModalContext";
import {
    Highlight,
    Snippet,
    useInstantSearch,
} from "react-instantsearch-hooks-web";

import { Admonition } from "components/Admonition/Admonition";
import { FaIcon } from "components/FaIcon/FaIcon";
import { Link } from "components/Link/Link";

import { HitObject } from "types/types";

import { insertBetween } from "utils/general";
import { track } from "utils/segment";

import "./SearchResults.scss";

type Breadcrumb = {
    spacer?: boolean;
    elipsis?: boolean;
    name?: string;
    path?: string;
};

type BreadcrumbsProps = {
    breadcrumbs: Array<Breadcrumb>;
    hit: HitObject;
};

function Breadcrumbs({ breadcrumbs = [], hit }: BreadcrumbsProps) {
    const { state } = useSearchModalContext();

    const last2Breadcrumbs = breadcrumbs.slice(-2);
    const allVisibleBreadcrumbs =
        breadcrumbs.length > 2
            ? [{ elipsis: true }, ...last2Breadcrumbs]
            : last2Breadcrumbs;
    const breadcrumbsWithSpacer = insertBetween<Breadcrumb>(
        { spacer: true },
        allVisibleBreadcrumbs
    );

    let productVersionInfo;
    if (state.versionSelectValue === "all") {
        let icon;
        let version;

        if (hit.product === "SingleStoreDB Cloud") {
            icon = <FaIcon icon={faCloud} mr="0-5x" />;
        } else if (hit.product === "SingleStoreDB Self-Managed") {
            icon = <FaIcon icon={faServer} mr="0-5x" />;
            version = `v${hit.version}`;
        }

        productVersionInfo = (
            <>
                <div className="hit-product-version">
                    {icon}
                    {hit.product} {version}
                </div>

                <FaIcon icon={faChevronRight} />
            </>
        );
    }

    return (
        <div className="breadcrumb-container">
            {productVersionInfo}

            <ol className="breadcrumb-list">
                {breadcrumbsWithSpacer.map(
                    ({ spacer, elipsis, name, path }: Breadcrumb, index) => {
                        if (spacer) {
                            return (
                                <li
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    className="breadcrumb-item decoration"
                                >
                                    /
                                </li>
                            );
                        }

                        if (elipsis) {
                            return (
                                <li
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    className="breadcrumb-item decoration"
                                >
                                    …
                                </li>
                            );
                        }

                        return (
                            <li key={path} className="breadcrumb-item">
                                {name}
                            </li>
                        );
                    }
                )}
            </ol>
        </div>
    );
}

const searchResultLink = cva("search-result-link", {
    variants: {
        focussed: {
            true: "focussed",
        },
    },
});

type SearchResultProps = {
    focussed: boolean;
    hit: any;
};

export function SearchResult({ focussed, hit }: SearchResultProps) {
    const linkClasses = searchResultLink({ focussed });
    const algoliaHit = Boolean(hit.objectID);

    const handleClick = () => {
        track("UI Element Clicked", {
            elementName: hit.title,
            elementType: "Search Result",
            destination: hit.path,
        });
    };

    let title = hit.title;
    let content = hit.content;
    if (algoliaHit) {
        title = (
            <Highlight highlightedTagName="span" attribute="title" hit={hit} />
        );
        content = <Snippet hit={hit} attribute="content" />;
    }

    return (
        <li className="hit-item">
            <Link
                to={hit.path}
                styled={false}
                className={linkClasses}
                onClick={handleClick}
            >
                <div className="content-container">
                    <Paragraph variant="body-3" className="hit-name">
                        {title}
                    </Paragraph>
                    <Paragraph variant="body-2" className="hit-content">
                        {content}
                    </Paragraph>
                    <Breadcrumbs breadcrumbs={hit.breadcrumbs} hit={hit} />
                </div>

                <div className="select-icon-container">
                    <FaIcon icon={faArrowTurnDownLeft} className="icon" />
                </div>
            </Link>
        </li>
    );
}

function SearchResultsCenteredText({ children }: React.PropsWithChildren) {
    return (
        <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="2x"
            height="full"
            px="2x"
        >
            {children}
        </Flex>
    );
}

type SearchResultsProps = {
    showingRecentlyViewed?: boolean;
    hits: any;
};

const searchResultsContainer = cva("search-results-container", {
    variants: {
        showingRecentlyViewed: {
            true: "showing-recently-viewed",
        },
    },
});

export function SearchResults({
    showingRecentlyViewed = false,
    hits,
}: SearchResultsProps) {
    const { state, dispatch } = useSearchModalContext();
    const { status } = useInstantSearch();

    // TODO: fix type definitions to work with algolia
    React.useEffect(() => {
        dispatch({
            type: "set_hits",
            value: hits as unknown as Array<HitObject>,
        });
    }, [dispatch, hits]);

    const computedStatus = React.useMemo(() => {
        if (state.isTyping || status === "loading") {
            return "loading";
        }

        return status;
    }, [state.isTyping, status]);

    let header;
    let banner;
    let content;

    if (computedStatus === "loading" || computedStatus === "stalled") {
        // TODO: add spinner here when we upgrade fusion
        content = (
            <SearchResultsCenteredText>
                <Paragraph variant="heading-3">Loading...</Paragraph>
            </SearchResultsCenteredText>
        );
    } else if (computedStatus === "error") {
        content = (
            <SearchResultsCenteredText>
                <Paragraph variant="heading-3">
                    Error loading your search results
                </Paragraph>
            </SearchResultsCenteredText>
        );
    } else {
        if (showingRecentlyViewed) {
            header = (
                <H2
                    variant="annotation-1"
                    textTransform="uppercase"
                    color="mid-contrast"
                    className="recently-viewed-heading"
                    fontWeight="bold"
                >
                    Recently Viewed
                </H2>
            );
        }

        if (state.is404Search) {
            banner = (
                <Admonition
                    title="That article doesn't exist for this product version"
                    className={box({ m: "1-5x" })}
                >
                    <Paragraph>
                        Review the pages below to find similar content or select
                        a different product version above.
                    </Paragraph>
                </Admonition>
            );
        }

        content = (
            <SearchResultsCenteredText>
                <Paragraph variant="heading-3">
                    No results were found for your query
                </Paragraph>
                <Paragraph variant="body-2">
                    Try adjusting your search query or selecting a different
                    product/version
                </Paragraph>
            </SearchResultsCenteredText>
        );

        if (showingRecentlyViewed) {
            content = (
                <SearchResultsCenteredText>
                    <Paragraph variant="heading-3">
                        No recently viewed pages
                    </Paragraph>
                </SearchResultsCenteredText>
            );
        }

        if (hits.length > 0) {
            content = (
                <ol className="hit-list">
                    {hits.map((hit: any, index: number) => {
                        const focussed = index === state.resultFocusIndex;

                        return (
                            <SearchResult
                                key={hit.path}
                                hit={hit}
                                focussed={focussed}
                            />
                        );
                    })}
                </ol>
            );
        }
    }

    const classes = searchResultsContainer({ showingRecentlyViewed });

    return (
        <div className={classes}>
            {header}
            {banner}
            {content}
        </div>
    );
}
